import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";
import { formatMoney } from "../lib/money";
import {
  Receipt, RotateCcw, ShieldCheck, MessageCircle,
} from "lucide-react";

/**
 * Sprint 4 (R4B hardened) — Delegate-facing Billing Center.
 *
 * Shows payment history + invoices ONLY.  Per Sprint R4B policy, there is
 * NO public refund-request form.  Diploks (and Diplomexa payments
 * generally) are non-refundable platform credits.  If a user believes
 * there is a payment-safety issue (duplicate charge, failed payment,
 * missing credit) they are directed to contact support, who can route
 * the case to the internal admin reconciliation surface.
 */
export default function BillingCenterPage() {
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const refresh = async () => {
    const [p, i] = await Promise.all([
      api.get("/payments/me"),
      api.get("/billing/invoices/me"),
    ]);
    setPayments(p.data.items);
    setInvoices(i.data.items);
  };
  useEffect(() => { refresh(); }, []);

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="billing-center-root">
        <header>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold flex items-center gap-1.5">
            <Receipt className="h-3 w-3" /> Billing Center
          </p>
          <h1 className="mt-1 text-2xl md:text-3xl font-bold text-[#0A192F]">
            Your payments &amp; invoices
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">
            All transactions are billed by either Factskoot Eduwellness Pvt Ltd
            (India) or Destineze Educational Voyages FZCO (UAE) depending on
            your billing currency. We never store card details.
          </p>
        </header>

        <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="billing-payments-section">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#0F4C81] font-bold mb-3">Payments</p>
          {payments.length === 0 ? (
            <Empty text="No payments yet. Browse pricing to get started." link={["/pricing", "See pricing"]} />
          ) : (
            <table className="w-full text-sm" data-testid="billing-payments-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="text-left py-1">Date</th>
                  <th className="text-left">Plan</th>
                  <th className="text-left">Billed by</th>
                  <th className="text-right">Total</th>
                  <th className="text-left">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100" data-testid={`billing-payment-${p.id}`}>
                    <td className="py-2 text-xs">{p.created_at?.slice(0, 10)}</td>
                    <td className="text-xs">{p.plan_id}</td>
                    <td className="text-xs">
                      {p.billing_entity === "factskoot" ? "Factskoot" : "Destineze"}
                    </td>
                    <td className="text-xs text-right font-medium">
                      {formatMoney(p.total_payable ?? p.amount, p.currency)}
                    </td>
                    <td className="text-xs">
                      <StatusPill status={p.status} />
                    </td>
                    <td className="text-xs text-right">
                      {p.invoice_number && (
                        <Link to={`/dashboard/billing/invoice/${p.invoice_number}`}
                              className="text-[#0F4C81] underline">Invoice</Link>
                      )}
                      {p.status === "failed" && (
                        <Link to={`/checkout?plan=${p.plan_id}&currency=${p.currency}`}
                              className="ml-3 text-[#0F4C81] underline flex items-center gap-1 inline-flex">
                          <RotateCcw className="h-3 w-3" /> Retry
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="billing-policy-section">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#0F4C81] font-bold mb-3 flex items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5" />
            Payment policy
          </p>
          <p className="text-sm text-slate-600">
            Diploks are platform credits used to unlock eligible Diplomexa
            services.  Once purchased, credited, redeemed or used, Diploks
            are <strong>generally non-refundable, non-transferable,
            non-withdrawable</strong> and cannot be converted into money,
            except where required by applicable law, payment-gateway rules,
            duplicate-payment correction, failed-transaction correction,
            gateway reversal, chargeback handling or approved administrative
            adjustment.
          </p>
          <p className="mt-3 text-xs text-slate-500">
            Believe you were charged twice, paid but never received Diploks,
            or saw an unexpected charge? Contact support and the team will
            review the transaction and make an administrative correction
            where applicable.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/dashboard/helpdesk"
              data-testid="billing-contact-support"
              className="inline-flex items-center gap-2 rounded-md bg-[#0A192F] px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-white hover:bg-[#102a4c]"
            >
              <MessageCircle className="h-3.5 w-3.5" /> Contact support
            </Link>
            <Link
              to="/refund-policy"
              data-testid="billing-policy-link"
              className="inline-flex items-center rounded-md border border-slate-300 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-700 hover:bg-slate-50"
            >
              Read policy
            </Link>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}

function Empty({ text, link }) {
  return (
    <div className="text-center py-8 text-xs text-slate-500">
      <p>{text}</p>
      {link && <Link to={link[0]} className="text-[#0F4C81] underline mt-2 inline-block">{link[1]}</Link>}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    paid: "bg-emerald-100 text-emerald-700",
    pending: "bg-amber-100 text-amber-700",
    processing: "bg-amber-100 text-amber-700",
    initiated: "bg-slate-100 text-slate-700",
    failed: "bg-rose-100 text-rose-700",
    refunded: "bg-blue-100 text-blue-700",
  };
  const cls = map[status] || "bg-slate-100 text-slate-700";
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold ${cls}`}>
      {status}
    </span>
  );
}
